import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Plus, User } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', name: '', phone: '',
    role: 'client_user', company_id: '', preferred_language: 'ko'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const res = await axios.get(`${API}/companies`);
    setCompanies(res.data);
  };

  const handleCreate = async () => {
    try {
      const payload = {...form};
      if (!payload.company_id) delete payload.company_id;
      await axios.post(`${API}/auth/register`, payload);
      toast.success('User created');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', phone: '', role: 'client_user', company_id: '', preferred_language: 'ko' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('users')}</h1>
          <p className="text-gray-500 mt-1">Manage system users</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('add')} {t('users')}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>{t('name')}</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="user-name" />
              </div>
              <div>
                <Label>{t('email')}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email" />
              </div>
              <div>
                <Label>{t('password')}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="user-password" />
              </div>
              <div>
                <Label>{t('phone')}</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operations_manager">Operations Manager</SelectItem>
                    <SelectItem value="client_user">Client User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === 'client_user' && (
                <div>
                  <Label>{t('company')}</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                    <SelectTrigger data-testid="user-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-user-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <p className="text-sm text-gray-600 text-center py-4">
          Demo users available. Add more users via the button above.
        </p>
      </Card>
    </div>
  );
}
